import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  CaretUp, CaretDown, Eye, EyeSlash, Trash, MagnifyingGlass, FloppyDisk,
  CircleNotch, Folder, FolderOpen, Plus,
} from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoriesPage = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newCat, setNewCat] = useState({ name: '', parent_id: '' });
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editCat, setEditCat] = useState({ id: '', name: '', description: '', parent_id: '' });
  const [saving, setSaving] = useState(false);

  const openEdit = (c) => {
    setEditCat({ id: c.id, name: c.name || '', description: c.description || '', parent_id: c.parent_id || '' });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editCat.name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/categories/${editCat.id}`, {
        name: editCat.name.trim(),
        description: editCat.description.trim(),
        parent_id: editCat.parent_id || null,
      }, { withCredentials: true });
      toast.success('Catégorie modifiée');
      setEditOpen(false);
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de modification');
    } finally { setSaving(false); }
  };

  const handleCreate = async () => {
    if (!newCat.name.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/categories`, {
        name: newCat.name.trim(),
        parent_id: newCat.parent_id || null,
      }, { withCredentials: true });
      toast.success('Catégorie créée');
      setNewCat({ name: '', parent_id: '' });
      setCreateOpen(false);
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de création');
    } finally { setCreating(false); }
  };

  const fetchCats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true });
      setCats(data);
    } catch (e) {
      toast.error('Erreur de chargement des catégories');
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchCats(); }, []);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const move = (idx, delta) => {
    setCats(prev => {
      const next = [...prev];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setPendingOrder(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await axios.post(`${API_URL}/api/admin/categories/reorder`,
        { ordering: cats.map(c => c.id) }, { withCredentials: true });
      toast.success('Ordre enregistré');
      setPendingOrder(false);
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setSavingOrder(false); }
  };

  const bulkVisibility = async (hide) => {
    if (!selected.size) return;
    try {
      await axios.post(`${API_URL}/api/admin/categories/bulk-visibility`,
        { category_ids: Array.from(selected), is_hidden: hide }, { withCredentials: true });
      toast.success(hide ? `${selected.size} catégorie(s) masquée(s)` : `${selected.size} catégorie(s) affichée(s)`);
      setSelected(new Set());
      fetchCats();
    } catch { toast.error('Erreur'); }
  };

  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Supprimer définitivement ${selected.size} catégorie(s) ? Les produits resteront mais perdront leur catégorie.`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/categories/bulk-delete`,
        { category_ids: Array.from(selected) }, { withCredentials: true });
      toast.success(`${data.deleted} catégorie(s) supprimée(s)`);
      setSelected(new Set());
      fetchCats();
    } catch { toast.error('Erreur de suppression'); }
  };

  const filtered = useMemo(() => {
    if (!search) return cats;
    const q = search.toLowerCase();
    return cats.filter(c => (c.name || '').toLowerCase().includes(q));
  }, [cats, search]);

  // Build a parent name lookup
  const nameById = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c.name])), [cats]);

  // Recursive descendants check (avoid putting under self/descendants on frontend too)
  const getDescendantIds = useMemo(() => {
    const childrenOf = {};
    cats.forEach(c => {
      if (c.parent_id) {
        (childrenOf[c.parent_id] = childrenOf[c.parent_id] || []).push(c.id);
      }
    });
    return (rootId) => {
      const out = new Set();
      const walk = (id) => {
        (childrenOf[id] || []).forEach(cid => {
          if (!out.has(cid)) { out.add(cid); walk(cid); }
        });
      };
      walk(rootId);
      return out;
    };
  }, [cats]);

  const setParent = async (catId, newParentId) => {
    const payload = { parent_id: newParentId === '__root__' ? null : newParentId };
    try {
      await axios.put(`${API_URL}/api/admin/categories/${catId}`, payload, { withCredentials: true });
      toast.success(payload.parent_id ? `Déplacée sous "${nameById[payload.parent_id]}"` : 'Devenue catégorie racine');
      fetchCats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de déplacement');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Catégories</h1>
          <p className="text-sm text-slate-500">{cats.length} catégorie(s) · {cats.filter(c => c.is_hidden).length} masquée(s)</p>
        </div>
        <div className="flex gap-2">
          {pendingOrder && (
            <Button onClick={saveOrder} disabled={savingOrder} className="gap-2" data-testid="save-order-btn">
              {savingOrder ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
              Enregistrer l'ordre
            </Button>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-cat-btn">
                <Plus size={16} /> Nouvelle catégorie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer une catégorie</DialogTitle>
                <DialogDescription>Ajoutez une nouvelle catégorie au catalogue. Optionnel : choisir une catégorie parente.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Nom *</Label>
                  <Input
                    value={newCat.name}
                    onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                    placeholder="Ex: Saucisson sec"
                    autoFocus
                    data-testid="new-cat-name"
                  />
                </div>
                <div>
                  <Label className="text-sm">Catégorie parente (optionnel)</Label>
                  <Select value={newCat.parent_id || '__root__'} onValueChange={(v) => setNewCat({ ...newCat, parent_id: v === '__root__' ? '' : v })}>
                    <SelectTrigger data-testid="new-cat-parent"><SelectValue placeholder="Aucune (racine)" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__root__">Aucune — catégorie racine</SelectItem>
                      {cats.filter(c => !c.parent_id).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreate} disabled={creating || !newCat.name.trim()} className="w-full" data-testid="save-new-cat">
                  {creating ? <CircleNotch size={16} className="animate-spin mr-2" /> : null}
                  Créer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Rechercher une catégorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-categories"
            />
          </div>
          {selected.size > 0 && (
            <div className="flex gap-2 items-center" data-testid="bulk-actions">
              <span className="text-sm text-slate-600">{selected.size} sélectionnée(s)</span>
              <Button variant="outline" size="sm" onClick={() => bulkVisibility(true)} className="gap-1.5" data-testid="bulk-hide">
                <EyeSlash size={14} /> Masquer
              </Button>
              <Button variant="outline" size="sm" onClick={() => bulkVisibility(false)} className="gap-1.5" data-testid="bulk-show">
                <Eye size={14} /> Afficher
              </Button>
              <Button variant="destructive" size="sm" onClick={bulkDelete} className="gap-1.5" data-testid="bulk-delete">
                <Trash size={14} /> Supprimer
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><CircleNotch size={32} className="animate-spin mx-auto text-slate-400" /></div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="grid grid-cols-[24px_40px_40px_1fr_220px_120px_120px] gap-3 px-4 py-2 bg-slate-50 text-[12px] font-semibold uppercase tracking-wider text-slate-500">
              <div></div>
              <div></div>
              <div></div>
              <div>Nom</div>
              <div>Parent</div>
              <div>Produits</div>
              <div>Statut</div>
            </div>
            {filtered.map((c, idx) => (
              <div key={c.id} className="grid grid-cols-[24px_40px_40px_1fr_220px_120px_120px] gap-3 px-4 py-3 hover:bg-slate-50 items-center" data-testid={`category-row-${c.id}`}>
                <Checkbox
                  checked={selected.has(c.id)}
                  onCheckedChange={() => toggleSelect(c.id)}
                  data-testid={`cat-cb-${c.id}`}
                />
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-up-${c.id}`}>
                  <CaretUp size={18} weight="bold" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === filtered.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`move-down-${c.id}`}>
                  <CaretDown size={18} weight="bold" />
                </button>
                <div className="flex items-center gap-2 min-w-0">
                  {c.parent_id ? <Folder size={16} className="text-slate-400 flex-shrink-0" /> : <FolderOpen size={16} className="text-amber-500 flex-shrink-0" />}
                  <button
                    onClick={() => openEdit(c)}
                    className={`truncate text-left hover:underline ${c.is_hidden ? 'text-slate-400 italic' : 'text-slate-800'} font-medium`}
                    data-testid={`cat-name-${c.id}`}
                    title="Cliquer pour modifier"
                  >
                    {c.name}
                  </button>
                </div>
                <div className="text-[12px] text-slate-500 truncate">
                  <Select
                    value={c.parent_id || '__root__'}
                    onValueChange={(v) => setParent(c.id, v)}
                  >
                    <SelectTrigger
                      className="h-7 text-[12px] px-2 border-slate-200 hover:border-slate-300"
                      data-testid={`parent-select-${c.id}`}
                    >
                      <SelectValue placeholder="Racine" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="__root__">— Racine —</SelectItem>
                      {(() => {
                        const banned = getDescendantIds(c.id);
                        banned.add(c.id);
                        return cats
                          .filter(p => !banned.has(p.id))
                          .map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.parent_id ? `↳ ${p.name}` : p.name}
                            </SelectItem>
                          ));
                      })()}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-[13px] font-semibold text-slate-700">{c.product_count}</div>
                <div className="flex items-center gap-2">
                  {c.is_hidden ? (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-[10px]">Masquée</Badge>
                  ) : (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-[10px]">Visible</Badge>
                  )}
                  <button
                    onClick={async () => {
                      const currentlyPromo = !!c.is_featured_promo;
                      if (currentlyPromo) {
                        if (!window.confirm(`Retirer la promo de "${c.name}" et de toutes ses sous-catégories ?`)) return;
                        try {
                          await axios.post(`${API_URL}/api/admin/categories/${c.id}/remove-promo`, {}, { withCredentials: true });
                          toast.success('Promo retirée');
                          fetchCats();
                        } catch { toast.error('Erreur'); }
                      } else {
                        const pct = window.prompt(`Pourcentage de remise à appliquer à TOUS les produits de "${c.name}" (et sous-catégories) ?\n\nExemple : 30 pour -30%`, '30');
                        if (!pct) return;
                        const discount = parseFloat(pct);
                        if (isNaN(discount) || discount <= 0 || discount > 90) {
                          toast.error('Pourcentage invalide (1-90)'); return;
                        }
                        const label = window.prompt(`Étiquette de la promo (ex: "Soldes d'été", "Black Friday"…)\nLaissez vide pour utiliser le nom de la catégorie.`, `Soldes d'été`);
                        try {
                          const { data } = await axios.post(`${API_URL}/api/admin/categories/${c.id}/apply-promo`,
                            { discount_percent: discount, label: label || null },
                            { withCredentials: true });
                          toast.success(`Promo -${discount}% appliquée à ${data.products_modified} produits`);
                          fetchCats();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                      }
                    }}
                    className="text-slate-400 hover:text-rose-600"
                    title={c.is_featured_promo ? 'Retirer la promo' : 'Mettre en promo (soldes…)'}
                    data-testid={`toggle-promo-${c.id}`}
                  >
                    {c.is_featured_promo
                      ? <Badge className="text-[10px] bg-rose-500 hover:bg-rose-600 text-white border-0">-{Math.round(c.promo_discount_percent || 0)}% {c.promo_label || 'PROMO'}</Badge>
                      : <span className="text-[15px]">%</span>}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/api/admin/categories/bulk-visibility`,
                          { category_ids: [c.id], is_hidden: !c.is_hidden }, { withCredentials: true });
                        toast.success(!c.is_hidden ? 'Catégorie masquée' : 'Catégorie affichée');
                        fetchCats();
                      } catch { toast.error('Erreur'); }
                    }}
                    className="text-slate-400 hover:text-slate-700"
                    title={c.is_hidden ? 'Afficher' : 'Masquer'}
                    data-testid={`toggle-vis-${c.id}`}
                  >
                    {c.is_hidden ? <EyeSlash size={16} className="text-amber-600" /> : <Eye size={16} className="text-emerald-600" />}
                  </button>
                </div>
              </div>
            ))}
            {!filtered.length && (
              <div className="p-8 text-center text-slate-400 text-sm">Aucune catégorie</div>
            )}
          </div>
        )}
      </Card>

      <p className="text-[12px] text-slate-500">
        💡 <b>Cliquez sur le nom d'une catégorie</b> pour la modifier.<br />
        💡 Réordonnez avec les flèches ↑↓. Cliquez "Enregistrer l'ordre" pour confirmer.<br />
        💡 Changez la colonne <b>Parent</b> pour transformer une catégorie en sous-catégorie d'une autre (ou la remettre à la racine).
        Les catégories masquées sont cachées du storefront mais conservent leurs produits.
      </p>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier la catégorie</DialogTitle>
            <DialogDescription>Renommez la catégorie ou changez son parent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Nom *</Label>
              <Input
                value={editCat.name}
                onChange={e => setEditCat(p => ({ ...p, name: e.target.value }))}
                placeholder="Nom de la catégorie"
                data-testid="edit-cat-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Description (facultatif)</Label>
              <Input
                value={editCat.description}
                onChange={e => setEditCat(p => ({ ...p, description: e.target.value }))}
                placeholder="Description courte"
                data-testid="edit-cat-description"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Catégorie parente</Label>
              <Select
                value={editCat.parent_id || '__root__'}
                onValueChange={(v) => setEditCat(p => ({ ...p, parent_id: v === '__root__' ? '' : v }))}
              >
                <SelectTrigger data-testid="edit-cat-parent"><SelectValue placeholder="Racine" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="__root__">— Racine —</SelectItem>
                  {(() => {
                    const banned = getDescendantIds(editCat.id);
                    banned.add(editCat.id);
                    return cats
                      .filter(p => !banned.has(p.id))
                      .map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.parent_id ? `↳ ${p.name}` : p.name}
                        </SelectItem>
                      ));
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-cat-cancel">Annuler</Button>
              <Button onClick={saveEdit} disabled={saving || !editCat.name.trim()} data-testid="edit-cat-save">
                {saving ? <><CircleNotch size={14} className="animate-spin mr-1.5" /> Enregistrement…</> : <><FloppyDisk size={14} className="mr-1.5" /> Enregistrer</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesPage;
